/**
 * @file shipping.ts
 *
 * Shipping API for the shop admin: custom shipping zones with their regions and
 * flat-rate/free-shipping methods, wired to the real commerce-engine `/v1` shipping
 * API. Reads map the `/v1` shapes to the design-reviewed WooCommerce-style
 * {@link ShippingZone} / {@link ShippingMethod} the screens use: the zones list
 * embeds each zone's locations (so there is no separate locations fetch), and the
 * typed method config is flattened into the `settings` map the existing forms read.
 * Writes map the same `settings` map back to the typed config: method creation sends
 * a minimal placeholder config (the contract requires it at create) that the caller's
 * immediate update fills in, and the update infers the immutable `shippingMethodType`
 * from the settings keys. The continent/country/state reference data is static and
 * built on the client (see `shipping/region-data.ts`); there is no geographic API.
 * Local-pickup locations live on a separate settings surface (see `shop-settings.ts`).
 */

import { v1Get, v1Mutate } from './v1-request';
import { minorUnitsToDecimal, toMoney } from './money';
import { getShopCurrency } from './shop';
import type {
  ShippingMethod,
  ShippingZone,
  ShippingZoneLocation,
  ShippingZoneMethodUpdate,
} from './shop-types';
import type {
  CreateShippingMethodRequestV1,
  CreateShippingZoneRequestV1,
  FreeShippingRequiresV1,
  ReorderShippingZonesRequestV1,
  ReplaceShippingZoneLocationsRequestV1,
  ShippingMethodResponseV1,
  ShippingMethodTypeV1,
  ShippingTaxStatusV1,
  ShippingZoneLocationTypeV1,
  ShippingZoneLocationV1,
  ShippingZoneResponseV1,
  UpdateShippingMethodRequestV1,
  UpdateShippingZoneRequestV1,
} from './v1-types';

/** A zone paired with its embedded location set (the `/v1` zones list carries both). */
export interface ShippingZoneWithLocations {
  zone: ShippingZone;
  locations: ShippingZoneLocation[];
}

/** `/v1` free-shipping requirement mode → the WooCommerce-style value the form uses. */
const V1_REQUIRES_TO_SHOP: Record<FreeShippingRequiresV1, string> = {
  none: '',
  min_amount: 'min_amount',
  coupon: 'coupon',
  min_or_coupon: 'either',
  min_and_coupon: 'both',
};

/** Display title per method type (the `/v1` method carries no type label). */
const METHOD_TITLE: Record<ShippingMethodTypeV1, string> = {
  flat_rate: 'Flat rate',
  free_shipping: 'Free shipping',
};

/** Maps a `/v1` zone resource to the WooCommerce-style {@link ShippingZone}. */
export function toShippingZone(zone: ShippingZoneResponseV1): ShippingZone {
  return { id: zone.id, name: zone.name, order: zone.sortOrder };
}

/**
 * Maps a zone's `/v1` location rows to the {@link ShippingZoneLocation}s the region
 * selector uses. `postcode` rows are dropped (the admin has no postcode UI; see
 * `backend-gaps.md`); continent/country/state codes pass straight through.
 *
 * @param locations - The zone's `/v1` location rows.
 *
 * @returns The continent/country/state locations.
 */
export function toShippingZoneLocations(
  locations: ShippingZoneLocationV1[],
): ShippingZoneLocation[] {
  return locations
    .filter((location) => location.type !== 'postcode' && location.code !== null)
    .map((location) => ({ code: location.code as string, type: location.type }));
}

/** Flattens a `/v1` method's typed config into the WooCommerce `settings` map the forms read. */
function toMethodSettings(method: ShippingMethodResponseV1): ShippingMethod['settings'] {
  const settings: ShippingMethod['settings'] = {
    title: { value: method.title, label: 'Method title' },
  };
  if (method.shippingMethodType === 'flat_rate') {
    settings.tax_status = { value: method.taxStatus, label: 'Tax status' };
    const base = minorUnitsToDecimal(method.baseCost ?? 0);
    const perItem = method.perItemCost ?? 0;
    settings.cost = {
      value: perItem > 0 ? `${base} + (${minorUnitsToDecimal(perItem)} * [qty])` : base,
      label: 'Cost',
    };
  } else if (method.shippingMethodType === 'free_shipping') {
    settings.requires = {
      value: V1_REQUIRES_TO_SHOP[method.requires ?? 'none'],
      label: 'Free shipping requires...',
    };
    if (method.minAmount != null) {
      settings.min_amount = {
        value: minorUnitsToDecimal(method.minAmount),
        label: 'Minimum order amount',
      };
    }
    if (method.ignoreDiscounts !== undefined) {
      settings.ignore_discounts = {
        value: method.ignoreDiscounts ? 'yes' : 'no',
        label: 'Apply minimum order rule before coupon discount',
      };
    }
  }
  return settings;
}

/**
 * Maps a `/v1` shipping method to the WooCommerce-style {@link ShippingMethod} pivot
 * (the typed config flattened into the `settings` map). Methods are delivery-only
 * (`flat_rate`/`free_shipping`); local pickup is a pickup_locations choice, not a method.
 *
 * @param method - The `/v1` shipping method resource.
 *
 * @returns The method in the design-reviewed `Shop*` shape.
 */
export function toShippingMethod(method: ShippingMethodResponseV1): ShippingMethod {
  return {
    instance_id: method.id,
    title: method.title,
    method_id: method.shippingMethodType,
    method_title: METHOD_TITLE[method.shippingMethodType] ?? method.shippingMethodType,
    enabled: method.enabled,
    settings: toMethodSettings(method),
  };
}

/**
 * Fetches all custom shipping zones with their embedded regions.
 *
 * @returns The zones (unordered; callers sort by `order`), each paired with its locations.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchShippingZones(): Promise<ShippingZoneWithLocations[]> {
  const res = await v1Get<ShippingZoneResponseV1[]>('/shipping/zones');
  return res.data.map((zone) => ({
    zone: toShippingZone(zone),
    locations: toShippingZoneLocations(zone.locations),
  }));
}

/**
 * Fetches the shipping methods on a zone.
 *
 * @param zoneId - The zone id.
 *
 * @returns The zone's methods (typed config flattened into the `settings` map).
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchShippingZoneMethods(
  zoneId: string,
): Promise<ShippingMethod[]> {
  const res = await v1Get<ShippingMethodResponseV1[]>(
    `/shipping/zones/${zoneId}/methods`,
  );
  return res.data.map(toShippingMethod);
}

/** WooCommerce-style free-shipping requirement value → the `/v1` requirement mode. */
const SHOP_REQUIRES_TO_V1: Record<string, FreeShippingRequiresV1> = {
  '': 'none',
  coupon: 'coupon',
  min_amount: 'min_amount',
  either: 'min_or_coupon',
  both: 'min_and_coupon',
};

/** Extracts the base cost from a flat-rate cost formula, dropping the `[qty]` fee term. */
function parseBaseCost(cost: string): string {
  const feeIndex = cost.indexOf(' + (');
  return (feeIndex >= 0 ? cost.slice(0, feeIndex) : cost).trim();
}

/** Extracts the per-item fee from a flat-rate cost formula, or `undefined` when absent. */
function parseQuantityFee(cost: string): string | undefined {
  const match = cost.match(/\+\s*\(\s*([0-9.]+)\s*\*\s*\[qty\]\s*\)/);
  return match ? match[1] : undefined;
}

/**
 * Infers a method's type from its WooCommerce settings keys: the `/v1` method PATCH
 * requires the immutable `shippingMethodType`, which the update payload doesn't carry,
 * so a `requires` key means free-shipping and otherwise flat-rate (the only two types
 * the form edits).
 */
function inferMethodType(settings: Record<string, string>): ShippingMethodTypeV1 {
  return 'requires' in settings ? 'free_shipping' : 'flat_rate';
}

/** Maps a WooCommerce settings map to the `/v1` typed method config for its type. */
function buildMethodConfig(
  type: ShippingMethodTypeV1,
  settings: Record<string, string>,
  currency: string,
): Partial<UpdateShippingMethodRequestV1> {
  if (type === 'free_shipping') {
    const config: Partial<UpdateShippingMethodRequestV1> = {
      requires: SHOP_REQUIRES_TO_V1[settings.requires ?? ''] ?? 'none',
    };
    if (settings.min_amount !== undefined) {
      config.minAmount = toMoney(settings.min_amount, currency).amount;
    }
    if (settings.ignore_discounts !== undefined) {
      config.ignoreDiscounts = settings.ignore_discounts === 'yes';
    }
    return config;
  }
  const cost = settings.cost ?? '0';
  const fee = parseQuantityFee(cost);
  return {
    baseCost: toMoney(parseBaseCost(cost), currency).amount,
    perItemCost: fee !== undefined ? toMoney(fee, currency).amount : 0,
    ...(settings.tax_status !== undefined
      ? { taxStatus: settings.tax_status as ShippingTaxStatusV1 }
      : {}),
  };
}

/**
 * Creates a shipping zone.
 *
 * @param name - The zone name.
 * @param sortOrder - The zone's position in the list — the caller passes the append
 *   index (the current zone count) so a new zone holds a distinct order instead of
 *   defaulting to 0 and colliding with every other zone (which then sort by id).
 *
 * @returns The created zone.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function createShippingZone(
  name: string,
  sortOrder: number,
): Promise<ShippingZone> {
  const body: CreateShippingZoneRequestV1 = { name, sortOrder };
  const res = await v1Mutate<ShippingZoneResponseV1>('/shipping/zones', 'POST', body);
  return toShippingZone(res);
}

/**
 * Renames a shipping zone.
 *
 * @param id - The zone id.
 * @param name - The new zone name.
 *
 * @returns The updated zone.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function updateShippingZoneName(
  id: string,
  name: string,
): Promise<ShippingZone> {
  const body: UpdateShippingZoneRequestV1 = { name };
  const res = await v1Mutate<ShippingZoneResponseV1>(`/shipping/zones/${id}`, 'PATCH', body);
  return toShippingZone(res);
}

/**
 * Reorders the shop's shipping zones in one bulk call.
 *
 * @param order - The `(id, sortOrder)` pairs in the desired order.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function reorderShippingZones(
  order: ReorderShippingZonesRequestV1['order'],
): Promise<void> {
  await v1Mutate<ShippingZoneResponseV1[]>('/shipping/zones/reorder', 'POST', { order });
}

/**
 * Soft-deletes a shipping zone.
 *
 * @param id - The zone id.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function deleteShippingZone(id: string): Promise<void> {
  await v1Mutate(`/shipping/zones/${id}`, 'DELETE');
}

/**
 * Replaces the full set of regions on a zone (the array sent becomes the zone's
 * complete location set; an empty array means "all regions").
 *
 * @param zoneId - The zone id.
 * @param locations - The complete desired location set (continent/country/state).
 *
 * @returns The saved locations.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function setShippingZoneLocations(
  zoneId: string,
  locations: ShippingZoneLocation[],
): Promise<ShippingZoneLocation[]> {
  const body: ReplaceShippingZoneLocationsRequestV1 = {
    locations: locations.map((location) => ({
      type: location.type as ShippingZoneLocationTypeV1,
      code: location.code,
    })),
  };
  const res = await v1Mutate<ShippingZoneResponseV1>(
    `/shipping/zones/${zoneId}/locations`,
    'PUT',
    body,
  );
  return toShippingZoneLocations(res.locations);
}

/**
 * Adds a shipping method to a zone. `/v1` requires the typed config at creation, so a
 * minimal valid placeholder config is sent for the type; the caller's immediate update
 * fills the real values.
 *
 * @param zoneId - The zone id.
 * @param methodId - The method type (`"flat_rate"` or `"free_shipping"`).
 *
 * @returns The created method (with its assigned `instance_id`).
 *
 * @throws {WcApiError} When the request fails.
 */
export async function addShippingZoneMethod(
  zoneId: string,
  methodId: string,
): Promise<ShippingMethod> {
  const dto: CreateShippingMethodRequestV1 =
    methodId === 'free_shipping'
      ? { shippingMethodType: 'free_shipping', title: METHOD_TITLE.free_shipping, requires: 'none' }
      : { shippingMethodType: 'flat_rate', title: METHOD_TITLE.flat_rate, baseCost: 0 };
  const res = await v1Mutate<ShippingMethodResponseV1>(
    `/shipping/zones/${zoneId}/methods`,
    'POST',
    dto,
  );
  return toShippingMethod(res);
}

/**
 * Updates a shipping method (its enabled flag, title, and/or settings). The `/v1`
 * method routes are zone-independent, so `zoneId` is used only by the store for keying.
 *
 * An enable/disable toggle carries no `settings`. The method PATCH requires the
 * **immutable** `shippingMethodType`, which can't be derived without the settings, so a
 * toggle is routed to the dedicated `POST /v1/shipping/methods/:id/enable` endpoint
 * instead — sending an inferred type would 422 a `free_shipping` method (type mismatch)
 * and reset a `flat_rate` method's costs to zero. When `settings` are provided (a config
 * save), the type is inferred from them (the form always sends the discriminating key)
 * and mapped back to the typed config via PATCH.
 *
 * @param zoneId - The zone id (unused by the request; kept for the store contract).
 * @param instanceId - The method id.
 * @param data - The method update (enabled/title/settings).
 *
 * @returns The updated method.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function updateShippingZoneMethod(
  zoneId: string,
  instanceId: string,
  data: ShippingZoneMethodUpdate,
): Promise<ShippingMethod> {
  // Enable/disable toggle (no settings): use the dedicated enable endpoint so we never
  // send a guessed immutable `shippingMethodType` or clobber the stored config.
  if (data.settings === undefined && data.enabled !== undefined) {
    const enabled = await v1Mutate<ShippingMethodResponseV1>(
      `/shipping/methods/${instanceId}/enable`,
      'POST',
      { enabled: data.enabled },
    );
    return toShippingMethod(enabled);
  }

  const settings = data.settings ?? {};
  const type = inferMethodType(settings);
  const currency = await getShopCurrency();
  const title = data.title ?? settings.title;
  const dto: UpdateShippingMethodRequestV1 = {
    shippingMethodType: type,
    ...(title !== undefined ? { title } : {}),
    ...(data.enabled !== undefined ? { enabled: data.enabled } : {}),
    // Persist the method's position within its zone when the caller supplies it (a save from
    // the zone form), so dragging methods into a new order actually sticks.
    ...(data.sortOrder !== undefined ? { sortOrder: data.sortOrder } : {}),
    ...buildMethodConfig(type, settings, currency),
  };
  const res = await v1Mutate<ShippingMethodResponseV1>(
    `/shipping/methods/${instanceId}`,
    'PATCH',
    dto,
  );
  return toShippingMethod(res);
}

/**
 * Soft-deletes a shipping method.
 *
 * @param zoneId - The zone id (unused by the request; kept for the store contract).
 * @param instanceId - The method id.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function deleteShippingZoneMethod(
  zoneId: string,
  instanceId: string,
): Promise<void> {
  await v1Mutate(`/shipping/methods/${instanceId}`, 'DELETE');
}
